"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OnboardingClientV2 = void 0;
const Qs = require("qs");
const base_client_1 = require("@deliverr/base-client");
const errors_1 = require("@deliverr/errors");
const client_auth_1 = require("@deliverr/client-auth");
const commons_utils_1 = require("@deliverr/commons-utils");
const core_auth_1 = require("@deliverr/core-auth");
const ChannelsEventType_1 = require("./ChannelsEventType");
const kinesis_1 = require("@deliverr/commons-aws/lib/kinesis");
const ListingSolution_1 = require("./ListingSolution");
const commons_aws_1 = require("@deliverr/commons-aws");
const commons_logs_1 = require("@deliverr/commons-logs");
const lodash_1 = require("lodash");
const uuid_1 = require("uuid");
const INVENTORY_UPDATE_STREAM_URL = (0, commons_utils_1.getEnvString)("INVENTORY_UPDATE_STREAM_URL", "https://kinesis.us-east-1.amazonaws.com");
const INVENTORY_UPDATE_STREAM_NAME = (0, commons_utils_1.getEnvString)("INVENTORY_UPDATE_STREAM_NAME", "inventory-update-mapper-stream");
class OnboardingClientV2 extends base_client_1.ApiClient {
    constructor(config = { authEngine: core_auth_1.AuthEngine.COGNITO_INTERNAL }, tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config), endpoint = "/v2", inventoryUpdateStream = new kinesis_1.KinesisClient(INVENTORY_UPDATE_STREAM_URL), triggerQueue = new commons_aws_1.Queue(process.env.CHANNELS_TRIGGER_QUEUE_URL || "http://localhost:9324/queue/channels-trigger-queue"), sellerMessageParallelFactor = (0, commons_utils_1.getEnvJson)("SELLER_MSG_PARALLEL_FACTOR", {})) {
        if (config.authEngine !== core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            commons_logs_1.log.warn("Non-CognitoInternal auth attempted");
        }
        const clientConfig = Object.assign(Object.assign({}, config), { baseURL: `${process.env.ONBOARDING_URL}${endpoint}`, authEngine: config.authEngine, tokenRefreshFn: tokenHandler.getTokenFn() });
        super(clientConfig);
        this.inventoryUpdateStream = inventoryUpdateStream;
        this.triggerQueue = triggerQueue;
        this.sellerMessageParallelFactor = sellerMessageParallelFactor;
    }
    async getOnboardingOrder(deliverrOrderId) {
        try {
            return await this.get(`/ecommerce/order/statusByDeliverrId`, { deliverrOrderId });
        }
        catch (error) {
            throw this.newOnboardingError("getOnboardingOrder", error, {
                deliverrOrderId,
            });
        }
    }
    async getOnboardingOrderBySellerOrderId(slsUuid, sellerOrderId) {
        try {
            return await this.get(`/ecommerce/order/statusBySellerId`, { slsUuid, sellerOrderId });
        }
        catch (error) {
            throw this.newOnboardingError("getOnboardingOrderBySellerOrderId", error, {
                slsUuid,
                sellerOrderId,
            });
        }
    }
    async deleteOnboardingOrder(slsUuid, sellerOrderId) {
        try {
            return await this.delete(`/ecommerce/order`, {
                slsUuid,
                sellerOrderId,
            });
        }
        catch (error) {
            throw this.newOnboardingError("deleteOnboardingOrder", error, {
                slsUuid,
                sellerOrderId,
            });
        }
    }
    async getAllListingSolutions() {
        const ctx = { fn: "getAllListingSolutions" };
        try {
            return this.get("listing-solution");
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting listing solutions");
            throw this.newOnboardingError("getAllListingSolutions", error);
        }
    }
    async getSellerIntegrations(sellerId) {
        try {
            return await this.get(`/seller/${sellerId}/integrations`);
        }
        catch (error) {
            throw this.newOnboardingError("getSellerIntegrations", error, {
                sellerId,
            });
        }
    }
    async updateSellerIntegrationAttributes(slsUuid, listingSolution, attributes) {
        try {
            return await this.post(`/${listingSolution}/updateAttributes/${slsUuid}`, attributes);
        }
        catch (error) {
            throw this.newOnboardingError("updateSellerIntegrationAttributes", error, {
                slsUuid,
                listingSolution,
            });
        }
    }
    async activateSellerIntegration(slsUuid) {
        try {
            return await this.post(`/channel/activate/${slsUuid}`, {});
        }
        catch (error) {
            throw this.newOnboardingError("activateSellerIntegration", error, {
                slsUuid,
            });
        }
    }
    async deactivateSellerIntegration(slsUuid) {
        try {
            return await this.post(`/channel/deactivate/${slsUuid}`, {});
        }
        catch (error) {
            throw this.newOnboardingError("deactivateSellerIntegration", error, {
                slsUuid,
            });
        }
    }
    async deactivateSalesChannel(slsUuid, salesChannelToBeDeactivated) {
        commons_logs_1.log.warn("deactivateSalesChannel is deprecated, use deactivateSellerIntegration instead", salesChannelToBeDeactivated);
        return this.deactivateSellerIntegration(slsUuid);
    }
    async getActiveAndValidIntegrationsByListingSolution(listingSolution, minCreatedAt, isOrderIngestionDisabled) {
        try {
            if (minCreatedAt && minCreatedAt instanceof Date) {
                minCreatedAt = minCreatedAt === null || minCreatedAt === void 0 ? void 0 : minCreatedAt.toISOString();
            }
            else if (minCreatedAt && isNaN(Date.parse(minCreatedAt))) {
                minCreatedAt = undefined;
                commons_logs_1.log.warn("minCreatedAt is not a valid date string, removing from request");
            }
            return this.get(`${listingSolution}/seller`, { minCreatedAt, isOrderIngestionDisabled });
        }
        catch (error) {
            throw this.newOnboardingError("getActiveAndValidIntegrationsByListingSolution", error, {
                listingSolution,
            });
        }
    }
    async getOauthConsentUrl(fullQueryString) {
        const ctx = { fn: "getOauthConsentUrl" };
        try {
            return await this.get(`/setup/oauth/url`, {
                fullQueryString,
            });
        }
        catch (error) {
            throw this.newOnboardingError("getOauthConsentUrl", error, Object.assign(Object.assign({}, ctx), { fullQueryString }));
        }
    }
    async getSellerChannelIdentityFromShopifyStore(shop) {
        const ctx = { fn: "getShopifyOauthConsentUrlForAppInstallation" };
        try {
            return await this.get(`/shopify/shop/${shop}/seller`);
        }
        catch (error) {
            throw this.newOnboardingError("getSellerChannelIdentityFromShopifyStore", error, Object.assign({}, ctx));
        }
    }
    async createChannelFromAppStore(listingSolution, sellerId, shop, generatedUUID) {
        const ctx = {
            fn: "createChannelFromAppStore",
            sellerId,
            listingSolution,
            shop,
            generatedUUID,
        };
        try {
            return await this.post(`/channel/app`, {
                sellerId,
                shop,
                name: listingSolution,
                uuid: generatedUUID,
            });
        }
        catch (error) {
            throw this.newOnboardingError("createChannelFromAppStore", error, Object.assign({}, ctx));
        }
    }
    async getApiKey(code) {
        const ctx = { fn: "getApiKey", code };
        try {
            return await this.get(`/apikey`, {
                code,
            });
        }
        catch (error) {
            throw this.newOnboardingError("getApiKey", error, ctx);
        }
    }
    async getSellerSalesChannels(sellerId) {
        try {
            return await this.get(`/seller/${sellerId}/channels`);
        }
        catch (error) {
            throw this.newOnboardingError("getSellerSalesChannels", error, {
                sellerId,
            });
        }
    }
    async createChannel(listingSolution, sellerId, data) {
        try {
            return await this.post(`/channel`, {}, Object.assign({ sellerId, name: listingSolution }, data));
        }
        catch (error) {
            const logData = Object.assign({ listingSolution, sellerId }, data);
            throw this.newOnboardingError("createChannel", error, logData);
        }
    }
    async updateChannel(slsUuid, data) {
        try {
            return await this.post(`/channel/${slsUuid}`, {}, Object.assign({}, data));
        }
        catch (error) {
            const logData = Object.assign({ slsUuid }, data);
            throw this.newOnboardingError("updateChannel", error, logData);
        }
    }
    async importCatalog(params) {
        try {
            return await this.post(`/product/import`, {}, params);
        }
        catch (error) {
            throw this.newOnboardingError("importCatalog", error, params);
        }
    }
    async syncInventoryFromQueue(inventories) {
        const ctx = { fn: "syncInventoryFromQueue", inventories };
        commons_logs_1.log.info(ctx);
        this.validateInventoryUpdates(ctx, inventories);
        try {
            return await this.post("/inventory/syncFromQueue", inventories);
        }
        catch (err) {
            throw this.newOnboardingError("syncInventoryFromQueue", err);
        }
    }
    async syncInventory(updates) {
        const ctx = { fn: "syncInventory", updates };
        commons_logs_1.log.info(ctx);
        this.validateInventoryUpdates(ctx, updates);
        updates.forEach((inventoryUpdate) => {
            if (inventoryUpdate.action) {
                if (!inventoryUpdate.channelId || !inventoryUpdate.channelProductId) {
                    throw new Error("Failed to sync inventory - action value provided but other fields were missing");
                }
            }
        });
        try {
            await this.streamInventoryUpdates(updates);
        }
        catch (err) {
            throw this.newOnboardingError("syncInventory", err);
        }
    }
    async createShippingOptionZones(slsUuid) {
        try {
            return await this.post(`/shipping-option-zones`, [], { slsUuid });
        }
        catch (error) {
            throw this.newOnboardingError("createShippingOptionZones", error, {
                slsUuid,
            });
        }
    }
    async getCredentials(slsUuid) {
        try {
            const response = await this.get(`/credentials`, { slsUuid });
            return response;
        }
        catch (error) {
            throw this.newOnboardingError("getCredentials", error, {
                slsUuid,
            });
        }
    }
    async getAccessToken(slsUuid) {
        try {
            return await this.get(`/credentials/${slsUuid}/access-token`);
        }
        catch (error) {
            throw this.newOnboardingError("getAccessToken", error, {
                slsUuid,
            });
        }
    }
    async getSellerHash(sellerId) {
        try {
            const response = await this.get(`/oauth/verifier`, { sellerId });
            return response;
        }
        catch (error) {
            throw this.newOnboardingError("getSellerHash", error, {
                sellerId,
            });
        }
    }
    async updateCredentials(listingSolution, sellerId, apiKey, apiSecret) {
        try {
            await this.post(`/${listingSolution}/credentials/${sellerId}`, {
                apiKey,
                apiSecret,
            });
        }
        catch (error) {
            throw this.newOnboardingError("updateCredentials", error, {
                listingSolution,
                sellerId,
            });
        }
    }
    async startCatalogUpdate(sendSellerEmailWhenDone) {
        try {
            const response = await this.post(`/jobs/product/sync`, {}, { email: sendSellerEmailWhenDone ? "true" : "" });
            return response;
        }
        catch (error) {
            throw this.newOnboardingError("startCatalogUpdate", error, {});
        }
    }
    async getCatalogUpdateStatus() {
        try {
            const response = await this.get(`/jobs/product/sync`, {});
            return response;
        }
        catch (error) {
            throw this.newOnboardingError("getCatalogUpdateStatus", error, {});
        }
    }
    async getCatalogUpdateStatusWithSlsUuid(slsUuid) {
        try {
            const response = await this.get(`/jobs/product/sync?slsUuid=${slsUuid}`, {});
            return response;
        }
        catch (error) {
            throw this.newOnboardingError("getCatalogUpdateStatusWithSlsUuid", error, {});
        }
    }
    async getShippingOptionZones(slsUuid) {
        try {
            const response = await this.get(`/shipping-option-zones`, { slsUuid });
            return response;
        }
        catch (error) {
            throw this.newOnboardingError("getShippingOptionZones", error, {
                slsUuid,
            });
        }
    }
    async updateShippingOptionZones(slsUuid, shippingOptionZones) {
        const ctx = { fn: "updateShippingOptionZones", shippingOptionZones };
        commons_logs_1.log.info(ctx);
        try {
            return await this.put(`/shipping-option-zones`, shippingOptionZones, {
                slsUuid,
            });
        }
        catch (error) {
            throw this.newOnboardingError("updateShippingOptionZones", error, {
                slsUuid,
            });
        }
    }
    async importProductBatch(importJobKey, slsUuid, importJobNextBatchVal) {
        const ctx = {
            fn: "importProductBatch",
            importJobKey,
            slsUuid,
            importJobNextBatchVal,
        };
        try {
            commons_logs_1.log.info(ctx);
            await this.post(`/product/updateImportJob`, { key: importJobKey, next: importJobNextBatchVal }, { slsUuid });
            commons_logs_1.log.info(Object.assign({}, ctx), "completed product import");
        }
        catch (error) {
            commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { error }), "error product import");
            throw this.newOnboardingError("importProductBatch", error, {
                importJobKey,
                slsUuid,
                importJobNextBatchVal,
            });
        }
    }
    async createOrders(slsUuid, orders) {
        try {
            return await this.post(`/orders/${slsUuid}`, orders);
        }
        catch (error) {
            throw this.newOnboardingError("createOrders", error);
        }
    }
    async syncOrders(slsUuid, orders) {
        try {
            return await this.post(`/orders/sync/${slsUuid}`, orders);
        }
        catch (error) {
            throw this.newOnboardingError("syncOrders", error);
        }
    }
    async acknowledgeOrders(slsUuid, orders) {
        try {
            return await this.post(`/orders/acknowledge/${slsUuid}`, orders);
        }
        catch (error) {
            throw this.newOnboardingError("acknowledgeOrders", error);
        }
    }
    async createProducts(slsUuid, products) {
        try {
            return await this.post(`/products/${slsUuid}`, products);
        }
        catch (error) {
            throw this.newOnboardingError("createProducts", error);
        }
    }
    async getOrderHistory(slsUuid, page, minOrderedAt, maxOrderedAt) {
        return this.request({
            url: `/orders/history`,
            params: { slsUuid, page, minOrderedAt, maxOrderedAt },
            paramsSerializer(params) {
                return Qs.stringify(params, { arrayFormat: "repeat" });
            },
            validateStatus: (status) => status > 0,
        });
    }
    async getNewOrders(slsUuid, page) {
        return this.getOrders(slsUuid, "new", page);
    }
    async getNewB2BOrders(slsUuid, page) {
        return this.getB2BOrders(slsUuid, "new", page);
    }
    async getRecentOrders(slsUuid, page) {
        return this.getOrders(slsUuid, "recent", page);
    }
    async getOrder(slsUuid, orderId) {
        const ctx = { fn: "getOrder", slsUuid };
        try {
            return await this.get(`/order/${slsUuid}/${orderId}`);
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting order");
            throw this.newOnboardingError("getOrder", error);
        }
    }
    async getOrderForCreation(slsUuid, orderId) {
        const ctx = { fn: "getOrder", slsUuid };
        try {
            return await this.get(`/order/creation/${slsUuid}/${orderId}`);
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting order");
            throw this.newOnboardingError("getOrder", error);
        }
    }
    async reportShipments(shipmentUpdates) {
        const ctx = { fn: "reportShipments" };
        try {
            const triggers = shipmentUpdates.map((update) => ({
                type: ChannelsEventType_1.ChannelsEventType.SHIPMENT_UPDATE,
                slsUuid: update.slsUuid,
                sellerId: update.sellerId,
                channel: update.listingSolutionId.toLowerCase(),
                payload: update.listingSolutionOrderId,
            }));
            await this.triggerQueue.batchEnqueue(triggers, this.getMessageGroupIdFunction(), () => (0, uuid_1.v4)());
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error enqueueing shipment updates");
            throw this.newOnboardingError("reportShipments", error);
        }
    }
    async reportCancellations(cancellationUpdates) {
        const ctx = { fn: "reportCancellations" };
        try {
            const triggers = cancellationUpdates.map((update) => ({
                type: ChannelsEventType_1.ChannelsEventType.CANCELLATION_UPDATE,
                slsUuid: update.slsUuid,
                sellerId: update.sellerId,
                channel: update.listingSolutionId.toLowerCase(),
                payload: update.listingSolutionOrderId,
            }));
            await this.triggerQueue.batchEnqueue(triggers, this.getMessageGroupIdFunction(), () => (0, uuid_1.v4)());
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error enqueueing cancellation updates");
            throw this.newOnboardingError("reportCancellations", error);
        }
    }
    async enqueueWebhookOrderEvent(sellerId, orderId, channel, slsUuid) {
        const ctx = { fn: "enqueueWebhookOrderEvent" };
        try {
            const trigger = {
                type: ChannelsEventType_1.ChannelsEventType.WEBHOOK_ORDER,
                sellerId,
                channel: channel.toLowerCase(),
                slsUuid,
                payload: orderId,
            };
            await this.triggerQueue.enqueue(trigger, this.getMessageGroupIdFunction()(trigger), (0, uuid_1.v4)());
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error enqueueing order events");
            throw this.newOnboardingError("enqueueWebhookOrderEvent", error);
        }
    }
    async enqueueOrderCreateEvent(sellerId, channel, slsUuid) {
        const ctx = { fn: "enqueueOrderCreateEvent" };
        try {
            const trigger = {
                type: ChannelsEventType_1.ChannelsEventType.ORDER_CREATE,
                sellerId,
                channel: channel.toLowerCase(),
                slsUuid,
            };
            await this.triggerQueue.enqueue(trigger, this.getMessageGroupIdFunction()(trigger), (0, uuid_1.v4)());
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error enqueueing order events");
            throw this.newOnboardingError("enqueueOrderCreateEvent", error);
        }
    }
    getMessageGroupIdFunction() {
        return (message) => {
            const sellerId = message.sellerId;
            const maxProcesses = sellerId in this.sellerMessageParallelFactor &&
                typeof this.sellerMessageParallelFactor[sellerId] === "number" &&
                this.sellerMessageParallelFactor[sellerId] >= 1
                ? this.sellerMessageParallelFactor[sellerId]
                : 1;
            const processId = (0, lodash_1.random)(1, maxProcesses, false);
            return `${message.sellerId}:${message.channel}:${message.type}` + ":" + processId;
        };
    }
    async getProducts(slsUuid, page) {
        const ctx = { fn: "getProducts", slsUuid };
        try {
            const response = await this.get(`/products`, {
                slsUuid,
                page,
            });
            return response;
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting products");
            throw this.newOnboardingError("getProducts", error);
        }
    }
    async getLatestProducts(slsUuid, page) {
        const ctx = { fn: "getLatestProducts", slsUuid };
        try {
            const response = await this.get(`/products`, {
                slsUuid,
                page,
                isIncremental: true,
            });
            return response;
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting latest products");
            throw this.newOnboardingError("getLatestProducts", error);
        }
    }
    async getSellerSalesChannel(slsUuid) {
        const ctx = { fn: "getSellerSalesChannel", slsUuid };
        try {
            return await this.get(`/channel`, {
                slsUuid,
            });
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting sales channel");
            throw this.newOnboardingError("getSellerSalesChannel", error);
        }
    }
    async updateInventory(update) {
        const ctx = { fn: "updateInventory" };
        try {
            await this.post(`/inventory/update`, update, {
                sellerId: update.sellerId,
                slsUuid: update.slsUuid,
            });
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error updating inventory");
            throw this.newOnboardingError("updateInventory", error);
        }
    }
    async streamInventoryUpdates(updates) {
        const ctx = { fn: "streamInventoryUpdates" };
        this.validateInventoryUpdates(ctx, updates);
        try {
            const now = new Date();
            const datedUpdates = updates.map((update) => (Object.assign(Object.assign({}, update), { createdAt: update.createdAt || now })));
            await this.inventoryUpdateStream.putRecords(INVENTORY_UPDATE_STREAM_NAME, datedUpdates, (update) => update.dsku);
            commons_logs_1.log.info(Object.assign(Object.assign({}, ctx), { updates }), "Streamed inventory updates");
        }
        catch (err) {
            throw this.newOnboardingError("streamInventoryUpdates", err, {
                updates: JSON.stringify(updates),
            });
        }
    }
    async getOrderReturnDetails(slsUuid, orderId) {
        const fn = "getOrderReturnDetails";
        const ctx = { fn, slsUuid, orderId };
        try {
            return await this.get(`/order/return/${slsUuid}/${orderId}`);
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting order return details");
            throw this.newOnboardingError(fn, error);
        }
    }
    async createOrderReturn(slsUuid, orderId, returnCreateData) {
        const fn = "createOrderReturn";
        const ctx = { fn, slsUuid, orderId, returnCreateData };
        try {
            return await this.post(`/order/return/${slsUuid}/${orderId}`, returnCreateData);
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error creating return");
            throw this.newOnboardingError(fn, error);
        }
    }
    async updateOrderReturnStatus(slsUuid, statusUpdateData) {
        const fn = "updateOrderReturnStatus";
        const ctx = { fn, slsUuid, statusUpdateData };
        try {
            return await this.post(`/order/return/status/${slsUuid}`, statusUpdateData);
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error updating return status");
            throw this.newOnboardingError(fn, error);
        }
    }
    async getRetailPricesForDsku(dsku) {
        const fn = "getRetailPricesForDsku";
        const ctx = { fn, dsku };
        try {
            return await this.get(`/retailprices/${dsku}`);
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting retail prices for DSKU");
            throw this.newOnboardingError("getRetailPricesForDsku", error);
        }
    }
    async registerDeliveryPromiseProvider({ sellerId, shop, channel = ListingSolution_1.ListingSolution.SHOPIFY, active = false, }) {
        try {
            await this.put(`/${channel}/promise/seller/${sellerId}/shop/${shop}/provider`, {}, { active });
        }
        catch (error) {
            throw this.newOnboardingError("registerDeliveryPromiseProvider", error, {
                sellerId,
                shop,
                channel,
            });
        }
    }
    async syncSkuToScheduleHandleBatch({ sellerId, shop, skuSettings, channel = ListingSolution_1.ListingSolution.SHOPIFY, }) {
        try {
            const res = await this.post(`/${channel}/promise/seller/${sellerId}/shop/${shop}/skus`, skuSettings);
            return res;
        }
        catch (error) {
            throw this.newOnboardingError("syncSkuToScheduleHandleBatch", error, {
                sellerId,
                shop,
                channel,
            });
        }
    }
    async getSkuToScheduleHandleBatchSyncStatus({ sellerId, shop, channel = ListingSolution_1.ListingSolution.SHOPIFY, }) {
        try {
            return this.get(`/${channel}/promise/seller/${sellerId}/shop/${shop}/skus/status`);
        }
        catch (error) {
            throw this.newOnboardingError("getSkuToScheduleHandleBatchSyncStatus", error, {
                sellerId,
                shop,
                channel,
            });
        }
    }
    async getShopPromiseProgram({ sellerId, shop, channel = ListingSolution_1.ListingSolution.SHOPIFY, }) {
        try {
            return this.get(`/${channel}/promise/seller/${sellerId}/shop/${shop}/enabled`);
        }
        catch (error) {
            throw this.newOnboardingError("getShopPromiseProgram", error, {
                sellerId,
                shop,
                channel,
            });
        }
    }
    async getDeliveryPromiseProvider({ sellerId, shop, channel = ListingSolution_1.ListingSolution.SHOPIFY, }) {
        try {
            return this.get(`/${channel}/promise/seller/${sellerId}/shop/${shop}/provider/fetch`);
        }
        catch (error) {
            throw this.newOnboardingError("getDeliveryPromiseProvider", error, {
                sellerId,
                shop,
                channel,
            });
        }
    }
    newOnboardingError(name, err, logData) {
        var _a;
        const ctx = Object.assign({ name }, logData);
        if (!err.response) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { err }));
            return new errors_1.DeliverrError({
                code: errors_1.ApiError.UNKNOWN_ERROR,
                functionName: name,
                payload: err,
            });
        }
        if (err.response && err.response.status !== undefined && err.response.status === 504) {
            const networkError = new errors_1.DeliverrError({
                code: errors_1.ApiError.NETWORK_ERROR,
                functionName: name,
            });
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { err: networkError }));
            return networkError;
        }
        commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { data: err.response.data }));
        const response = err.response;
        return new errors_1.DeliverrError({
            code: ((_a = response.data) === null || _a === void 0 ? void 0 : _a.code) || errors_1.ApiError.UNKNOWN_ERROR,
            subcode: String(err.response.status),
            functionName: name,
            payload: err.response.data,
        });
    }
    validateInventoryUpdate(ctx, inventoryUpdate) {
        if (inventoryUpdate.channelId && !inventoryUpdate.slsUuid) {
            commons_logs_1.log.error({ ctx, inventoryUpdate }, "InventoryUpdate: missing slsUuid");
        }
        if (inventoryUpdate.action) {
            if (!inventoryUpdate.channelId || !inventoryUpdate.channelProductId || !inventoryUpdate.slsUuid) {
                commons_logs_1.log.error({ ctx, inventoryUpdate }, "InventoryUpdate: action value provided but other fields were missing");
            }
        }
    }
    validateInventoryUpdates(ctx, inventoryUpdates) {
        inventoryUpdates.forEach((inventoryUpdate) => this.validateInventoryUpdate(ctx, inventoryUpdate));
    }
    async getOrders(slsUuid, status, page) {
        const ctx = { fn: "getOrders", slsUuid, status };
        try {
            const response = await this.get(`/orders`, {
                slsUuid,
                status,
                page,
            });
            return response;
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting orders");
            throw this.newOnboardingError("getOrders", error);
        }
    }
    async getB2BOrders(slsUuid, status, page) {
        const ctx = { fn: "getB2BOrders", slsUuid, status };
        try {
            const response = await this.get(`/orders/b2b`, {
                slsUuid,
                status,
                page,
            });
            return response;
        }
        catch (error) {
            commons_logs_1.log.error(Object.assign(Object.assign({}, ctx), { error }), "error getting orders");
            throw this.newOnboardingError("getOrders", error);
        }
    }
}
exports.OnboardingClientV2 = OnboardingClientV2;
//# sourceMappingURL=OnboardingClientV2.js.map